import * as React from 'react';
import BPromise from 'bluebird';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { FILTER_TYPE, FIRST_PAGE, DEFAULT_SKIP } from 'Src/consts/table';
import { ColumnDefinition, getServerQueryFilter2, TABLE_SORT_DIRECTION } from 'Src/helpers/table/table';
import { ROLE, ROLE_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/user';
import { getSuperAdminSchools } from 'Src/helpers/service/superadmin/schools';
import { getSuperAdminSchoolStudents } from 'Src/helpers/service/superadmin/students';
import { getSuperAdminSchoolSports, SuperAdminSport } from 'Src/helpers/service/superadmin/sports';
import {
  createSuperAdminUserPermission,
  deleteSuperAdminUserPermission,
  getAllSuperAdminUserPermissions,
  getSuperAdminUserPermissions,
  getSuperAdminUserPermissionsCount,
  SuperAdminUserPermission,
  SuperAdminUserPermissionCreateData,
  SuperAdminUserPermissionUpdateData,
  updateSuperAdminUserPermission
} from 'Src/helpers/service/superadmin/users';

const INITIAL_PERMISSION_FILTERS = {
  status: []
};

const PERMISSION_STATUS_OPTIONS = [
  { text: 'Active', value: 'ACTIVE' },
  { text: 'Blocked', value: 'BLOCKED' },
  { text: 'Archived', value: 'ARCHIVED' },
  { text: 'Removed', value: 'REMOVED' }
];

const SCHOOL_PERMISSION_PRESETS = [
  ROLE.ADMIN,
  ROLE.MANAGER,
  ROLE.TEACHER,
  ROLE.COACH,
  ROLE.PARENT,
  ROLE.STUDENT,
  ROLE.GOVERNOR
];
const ROLE_OPTIONS_FALLBACK = [ROLE.ADMIN, ROLE.TEACHER, ROLE.COACH, ROLE.PARENT, ROLE.STUDENT];
const STAFF_ROLE_PRESETS = [ROLE.ADMIN, ROLE.MANAGER, ROLE.TEACHER, ROLE.COACH];

const PERMISSION_OPTIONS = {
  status: PERMISSION_STATUS_OPTIONS
};

interface NamedEntity {
  id?: string;
  _id?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  allowedPermissionPresets?: any;
  kind?: string;
  form?: any;
  house?: any;
  postcode?: any;
  address?: string;
  pic?: string;
  [key: string]: any;
}

interface PermissionForm {
  preset: string;
  status: string;
  priority: string;
  activatedAt: string;
  deactivatedAt: string;
  comment: string;
  detailsText: string;
  tournamentId: string;
  schoolId: string;
  studentId: string;
  sportIds: string[];
  sports: SuperAdminSport[];
  isExcludedFromMisIntegration: boolean;
  school?: NamedEntity;
  student?: NamedEntity;
}

const EMPTY_PERMISSION_FORM: PermissionForm = {
  preset: '',
  status: 'ACTIVE',
  priority: '',
  activatedAt: '',
  deactivatedAt: '',
  comment: '',
  detailsText: '',
  tournamentId: '',
  schoolId: '',
  studentId: '',
  sportIds: [],
  sports: [],
  isExcludedFromMisIntegration: false,
  school: undefined,
  student: undefined
};

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function formatDateTime(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatDateTimeForInput(value?: string): string {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return '';
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(
    date.getMinutes()
  )}`;
}

function inputDateTimeToIso(value: string): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);

  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date.toISOString();
}

function formatValue(value?: string): string {
  return value
    ? value
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, letter => letter.toUpperCase())
    : '';
}

function getPreset(permission: SuperAdminUserPermission): string {
  const preset = permission.preset || '';
  return ROLE_SERVER_TO_CLIENT_MAPPING[preset] || formatValue(preset);
}

function getStatus(permission: SuperAdminUserPermission): string {
  return formatValue(permission.status);
}

function getSchool(permission: SuperAdminUserPermission): string {
  const school = permission.school;
  const name = school && school.name ? school.name : '';
  const id = school && school.id ? school.id : permission.schoolId;
  const kind = school && school.kind === 'SchoolUnion' ? 'School union' : '';

  return [name, kind, id].filter(Boolean).join(' / ');
}

function getStudent(permission: SuperAdminUserPermission): string {
  const student = permission.student;

  if (student) {
    if (student.name) {
      return student.name;
    }

    const name = [student.firstName, student.lastName].filter(Boolean).join(' ');

    if (name) {
      return name;
    }

    if (student.id) {
      return student.id;
    }
  }

  return permission.studentId || '';
}

function getSports(permission: SuperAdminUserPermission): string {
  if (permission.sports && permission.sports.length > 0) {
    return permission.sports
      .map(sport => sport.name || sport.id)
      .filter(Boolean)
      .join(', ');
  }

  if (permission.sport && permission.sport.name) {
    return permission.sport.name;
  }

  if (permission.sport && permission.sport.id) {
    return permission.sport.id;
  }

  return Array.isArray(permission.sportIds) ? permission.sportIds.join(', ') : '';
}

function getPriority(permission: SuperAdminUserPermission): string {
  return typeof permission.priority === 'number' ? String(permission.priority) : '';
}

function getActivatedAt(permission: SuperAdminUserPermission): string {
  return formatDateTime(permission.activatedAt);
}

function getDeactivatedAt(permission: SuperAdminUserPermission): string {
  return formatDateTime(permission.deactivatedAt);
}

function getCreatedAt(permission: SuperAdminUserPermission): string {
  return formatDateTime(permission.createdAt);
}

function getUpdatedAt(permission: SuperAdminUserPermission): string {
  return formatDateTime(permission.updatedAt);
}

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Preset',
    field: 'preset',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getPreset
  },
  {
    text: 'MIS ID',
    field: 'misId',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['misId']
  },
  {
    text: 'Status',
    field: 'status',
    isSort: false,
    type: FILTER_TYPE.MULTISELECT,
    accessor: getStatus
  },
  {
    text: 'School / union',
    field: 'school',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSchool
  },
  {
    text: 'Student',
    field: 'student',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getStudent
  },
  {
    text: 'Sport',
    field: 'sport',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getSports
  },
  {
    text: 'Priority',
    field: 'priority',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getPriority
  },
  {
    text: 'Activated',
    field: 'activatedAt',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getActivatedAt
  },
  {
    text: 'Deactivated',
    field: 'deactivatedAt',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getDeactivatedAt
  },
  {
    text: 'Created',
    field: 'createdAt',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getCreatedAt
  },
  {
    text: 'Updated',
    field: 'updatedAt',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getUpdatedAt
  }
];

interface Props {
  userId: string;
}

interface State {
  items: SuperAdminUserPermission[];
  existingPermissions: SuperAdminUserPermission[];
  selectedItems: SuperAdminUserPermission[];
  currentPage: number;
  total: number;
  filters: any;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  isSelectAllChecked: boolean;
  isLoading: boolean;
  isSaving: boolean;
  errorMessage: string;
  successMessage: string;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  form: PermissionForm;
}

function getPermissionsWhere(filters: any): any {
  const where: any = {};
  const status = filters.status;

  if (Array.isArray(status) && status.length > 0) {
    where.status = { $in: status };
  }

  return where;
}

function getPermissionId(permission?: SuperAdminUserPermission): string {
  return permission ? permission.id || permission._id || '' : '';
}

function getEntityId(entity?: NamedEntity, fallback?: string): string {
  return entity && (entity.id || entity._id) ? String(entity.id || entity._id) : fallback || '';
}

function getPermissionSchoolId(permission: SuperAdminUserPermission): string {
  return (
    permission.schoolId ||
    (permission.school && permission.school.id) ||
    (permission.school && permission.school._id) ||
    ''
  );
}

function getPermissionStudentId(permission: SuperAdminUserPermission): string {
  return (
    permission.studentId ||
    (permission.student && permission.student.id) ||
    (permission.student && permission.student._id) ||
    ''
  );
}

function isPermissionStillLinked(permission: SuperAdminUserPermission): boolean {
  return permission.status !== 'REMOVED' && permission.status !== 'ARCHIVED';
}

function isStaffRole(preset: string): boolean {
  return STAFF_ROLE_PRESETS.indexOf(preset as any) !== -1;
}

function hasDuplicateStaffRole(
  permissions: SuperAdminUserPermission[],
  schoolId: string,
  preset: string,
  ignoredPermissionId?: string
): boolean {
  if (!schoolId || !preset || !isStaffRole(preset)) {
    return false;
  }

  return permissions.some(permission => {
    const permissionId = getPermissionId(permission);

    if (ignoredPermissionId && permissionId === ignoredPermissionId) {
      return false;
    }

    return (
      isPermissionStillLinked(permission) &&
      getPermissionSchoolId(permission) === schoolId &&
      permission.preset === preset
    );
  });
}

function getLinkedParentStudentIds(
  permissions: SuperAdminUserPermission[],
  schoolId: string,
  ignoredPermissionId?: string
): string[] {
  return permissions
    .filter(permission => {
      const permissionId = getPermissionId(permission);

      if (ignoredPermissionId && permissionId === ignoredPermissionId) {
        return false;
      }

      return (
        isPermissionStillLinked(permission) &&
        permission.preset === ROLE.PARENT &&
        getPermissionSchoolId(permission) === schoolId
      );
    })
    .map(getPermissionStudentId)
    .filter(Boolean);
}

function getEntityName(entity?: NamedEntity): string {
  if (!entity) {
    return '';
  }

  const name = entity.name || [entity.firstName, entity.lastName].filter(Boolean).join(' ');
  return name || getEntityId(entity);
}

function getSchoolTitle(school: NamedEntity): string {
  const postcode = school && school.postcode && school.postcode.postcode ? school.postcode.postcode : '';
  const kind = school && school.kind === 'SchoolUnion' ? 'School union' : '';
  return [getEntityName(school), kind, postcode].filter(Boolean).join(' / ');
}

function getStudentTitle(student: NamedEntity): string {
  const name = getEntityName(student);
  const formName = student && student.form && student.form.name ? student.form.name : '';
  const houseName = student && student.house && student.house.name ? student.house.name : '';
  return [name, formName, houseName].filter(Boolean).join(' / ');
}

function isTeacherOrCoach(preset: string): boolean {
  return preset === ROLE.TEACHER || preset === ROLE.COACH;
}

function isParent(preset: string): boolean {
  return preset === ROLE.PARENT;
}

function isStaff(preset: string): boolean {
  return [ROLE.ADMIN, ROLE.MANAGER, ROLE.TEACHER, ROLE.COACH, ROLE.GOVERNOR].indexOf(preset) !== -1;
}

function permissionToForm(permission: SuperAdminUserPermission): PermissionForm {
  const details = permission.details;
  const school = permission.school as NamedEntity;
  const student = permission.student as NamedEntity;
  const sports = permission.sports
    ? permission.sports.map((sport: any) => ({
        ...sport,
        id: sport.id || sport._id
      }))
    : [];
  const sportIds = Array.isArray(permission.sportIds)
    ? permission.sportIds
    : sports
        .map((sport: any) => sport.id || sport._id)
        .filter(Boolean)
        .map(String);

  return {
    preset: permission.preset || '',
    status: permission.status || 'ACTIVE',
    schoolId: getEntityId(school, permission.schoolId),
    studentId: getEntityId(student, permission.studentId),
    sportIds,
    sports,
    tournamentId: permission.tournamentId || '',
    priority: typeof permission.priority === 'number' ? String(permission.priority) : '',
    activatedAt: formatDateTimeForInput(permission.activatedAt),
    deactivatedAt: formatDateTimeForInput(permission.deactivatedAt),
    comment: permission.comment || '',
    detailsText: details ? JSON.stringify(details, null, 2) : '',
    isExcludedFromMisIntegration: permission.isExcludedFromMisIntegration === true,
    school,
    student
  };
}

function setIfString(target: any, field: string, value: string): void {
  const trimmedValue = value.trim();

  if (trimmedValue) {
    target[field] = trimmedValue;
  }
}

function buildCreateData(form: PermissionForm): SuperAdminUserPermissionCreateData {
  const data: SuperAdminUserPermissionCreateData = {
    preset: form.preset,
    status: form.status,
    schoolId: form.schoolId,
    isExcludedFromMisIntegration: form.isExcludedFromMisIntegration
  };

  const activatedAt = inputDateTimeToIso(form.activatedAt);
  const deactivatedAt = inputDateTimeToIso(form.deactivatedAt);

  if (isParent(form.preset)) {
    data.studentId = form.studentId;
    if (form.priority !== '') {
      data.priority = Number(form.priority);
    }
  }

  if (isTeacherOrCoach(form.preset) && form.sportIds.length > 0) {
    data.sportIds = form.sportIds;
  }

  if (activatedAt) {
    data.activatedAt = activatedAt;
  }

  if (deactivatedAt) {
    data.deactivatedAt = deactivatedAt;
  }

  setIfString(data, 'comment', form.comment);
  setIfString(data, 'tournamentId', form.tournamentId);

  if (form.detailsText.trim()) {
    data.details = JSON.parse(form.detailsText);
  }

  return data;
}

function buildUpdateData(form: PermissionForm): SuperAdminUserPermissionUpdateData {
  const data: SuperAdminUserPermissionUpdateData = {
    status: form.status,
    isExcludedFromMisIntegration: form.isExcludedFromMisIntegration
  };

  const activatedAt = inputDateTimeToIso(form.activatedAt);
  const deactivatedAt = inputDateTimeToIso(form.deactivatedAt);

  if (activatedAt) {
    data.activatedAt = activatedAt;
  }

  if (deactivatedAt) {
    data.deactivatedAt = deactivatedAt;
  }

  if (isTeacherOrCoach(form.preset)) {
    data.sportIds = form.sportIds;
  }

  if (form.priority !== '') {
    data.priority = Number(form.priority);
  }

  return data;
}

function normalizeList(items: any[]): any[] {
  return (items || []).map(item => ({ ...item, id: item.id || item._id }));
}

function getAllowedRoleValues(
  school: NamedEntity | undefined,
  currentPermissions: SuperAdminUserPermission[]
): string[] {
  if (!school) {
    return [];
  }

  const allowedPermissionPresets = school.allowedPermissionPresets;
  const schoolId = getEntityId(school);
  let roles: string[] = [];

  if (school.kind === 'SchoolUnion') {
    roles = [ROLE.ADMIN];
  } else if (allowedPermissionPresets) {
    roles = SCHOOL_PERMISSION_PRESETS.filter(role => Boolean(allowedPermissionPresets[role]));
  } else {
    roles = ROLE_OPTIONS_FALLBACK.slice();
  }

  return roles.filter(role => {
    if (!isStaffRole(role)) {
      return true;
    }

    return !hasDuplicateStaffRole(currentPermissions, schoolId, role);
  });
}

export class SuperAdminUserPermissions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      existingPermissions: [],
      selectedItems: [],
      currentPage: FIRST_PAGE,
      total: 0,
      filters: { ...INITIAL_PERMISSION_FILTERS },
      isShowFilter: false,
      isDataFiltered: false,
      isSelectAllChecked: false,
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      successMessage: '',
      isCreateModalOpen: false,
      isEditModalOpen: false,
      form: { ...EMPTY_PERMISSION_FORM }
    };
  }

  componentDidMount() {
    this.setItems(FIRST_PAGE);
  }

  setItems = (page: number): void => {
    const { userId } = this.props;
    const { filters } = this.state;
    const where = getPermissionsWhere(filters);
    const serverQueryFilter = getServerQueryFilter2(page, where, 'createdAt DESC');

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    BPromise.all([
      getSuperAdminUserPermissionsCount(userId, where),
      getSuperAdminUserPermissions(userId, serverQueryFilter),
      getAllSuperAdminUserPermissions(userId)
    ])
      .then(([countObj, items, existingPermissions]) => {
        this.setState({
          items,
          existingPermissions,
          total: countObj.count,
          currentPage: page,
          isLoading: false,
          errorMessage: '',
          isDataFiltered: Object.keys(where).length > 0,
          selectedItems: [],
          isSelectAllChecked: false
        });
      })
      .catch(error => {
        console.error('Can not load superadmin user permissions', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load permissions. Please check the session and try again.'
        });
      });
  };

  setCurrentPageParams = (currentPage: number): void => {
    this.setItems(currentPage);
  };

  searchSchools = (text: string): Promise<NamedEntity[]> => {
    const filter = {
      where: {
        kind: { $in: ['School', 'SchoolUnion'] },
        name: {
          like: text || '',
          options: 'i'
        }
      },
      limit: 10,
      skip: DEFAULT_SKIP,
      order: 'name ASC'
    };

    return getSuperAdminSchools(filter).then(normalizeList);
  };

  searchStudents = (text: string): Promise<NamedEntity[]> => {
    const { form } = this.state;

    if (!form.schoolId) {
      return Promise.resolve([]);
    }

    const filter = {
      where: text
        ? {
            $or: [{ firstName: { like: text, options: 'i' } }, { lastName: { like: text, options: 'i' } }]
          }
        : {},
      limit: 10,
      skip: DEFAULT_SKIP,
      order: 'lastName ASC'
    };

    const linkedStudentIds = getLinkedParentStudentIds(this.state.existingPermissions, form.schoolId);

    return getSuperAdminSchoolStudents(form.schoolId, filter)
      .then(normalizeList)
      .then(students =>
        students.filter(student => {
          const studentId = getEntityId(student);
          return linkedStudentIds.indexOf(studentId) === -1;
        })
      );
  };

  searchSports = (text: string): Promise<SuperAdminSport[]> => {
    const { form } = this.state;

    if (!form.schoolId) {
      return Promise.resolve([]);
    }

    const filter = {
      where: {
        name: {
          like: text || '',
          options: 'i'
        },
        id: {
          $nin: form.sportIds
        }
      },
      limit: 10,
      skip: DEFAULT_SKIP,
      order: 'name ASC'
    };

    return getSuperAdminSchoolSports(form.schoolId, filter).then(normalizeList);
  };

  onSchoolSelect = (school: NamedEntity): void => {
    const schoolId = getEntityId(school);

    this.setState({
      form: {
        ...this.state.form,
        school,
        schoolId,
        preset: '',
        student: undefined,
        studentId: '',
        sports: [],
        sportIds: []
      },
      errorMessage: '',
      successMessage: ''
    });
  };

  onStudentSelect = (student: NamedEntity): void => {
    this.setState({
      form: {
        ...this.state.form,
        student,
        studentId: getEntityId(student)
      },
      errorMessage: '',
      successMessage: ''
    });
  };

  onSportSelect = (sport: SuperAdminSport): void => {
    const sportId = getEntityId(sport as NamedEntity);
    const { form } = this.state;

    if (!sportId || form.sportIds.indexOf(sportId) !== -1) {
      return;
    }

    this.setState({
      form: {
        ...form,
        sportIds: [...form.sportIds, sportId],
        sports: [...form.sports, { ...sport, id: sportId }]
      },
      errorMessage: '',
      successMessage: ''
    });
  };

  onSportRemove = (index: number): void => {
    const { form } = this.state;
    const sportIds = form.sportIds.slice();
    const sports = form.sports.slice();

    sportIds.splice(index, 1);
    sports.splice(index, 1);

    this.setState({
      form: {
        ...form,
        sportIds,
        sports
      }
    });
  };

  onRoleChange = (event): void => {
    const preset = event.target.value;
    const { form, existingPermissions } = this.state;

    if (hasDuplicateStaffRole(existingPermissions, form.schoolId, preset)) {
      this.setState({
        form: {
          ...form,
          preset: '',
          student: undefined,
          studentId: '',
          sports: [],
          sportIds: [],
          priority: ''
        },
        errorMessage: 'This user already has this staff role in the selected school.',
        successMessage: ''
      });
      return;
    }

    this.setState({
      form: {
        ...form,
        preset,
        student: undefined,
        studentId: '',
        sports: [],
        sportIds: [],
        priority: isParent(preset) ? form.priority : ''
      },
      errorMessage: '',
      successMessage: ''
    });
  };

  onTableFilterChange = (event, filterField: string): void => {
    const filter = COLUMNS.find(column => column.field === filterField);
    const filterType = filter && filter.type;
    let value: any = event.target.value;

    if (filterType === FILTER_TYPE.MULTISELECT) {
      value = [];
      const selectOptions = event.target.options;

      for (let i = 0; i < selectOptions.length; i++) {
        if (selectOptions[i].selected) {
          value.push(selectOptions[i].value);
        }
      }
    }

    this.setState({
      filters: {
        ...this.state.filters,
        [filterField]: value
      }
    });
  };

  onApplyFilterClick = (): void => {
    this.setItems(FIRST_PAGE);
  };

  onClearFilterClick = (): void => {
    this.setState({ filters: { ...INITIAL_PERMISSION_FILTERS } }, () => this.setItems(FIRST_PAGE));
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();
    this.setState({ isShowFilter: !this.state.isShowFilter });
  };

  onTableSortClick = (): void => undefined;

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemId = getPermissionId(selectedItem);
    const selectedItemIndex = selectedItems.findIndex(item => getPermissionId(item) === selectedItemId);

    if (selectedItemIndex === -1) {
      this.setState({ selectedItems: [selectedItem], successMessage: '' });
    } else {
      this.setState({ selectedItems: [], successMessage: '' });
    }
  };

  onSelectAllClick = (): void => {
    const where = getPermissionsWhere(this.state.filters);

    this.setState({ isLoading: true, errorMessage: '', successMessage: '' });

    getAllSuperAdminUserPermissions(this.props.userId, where)
      .then(items => {
        this.setState({
          selectedItems: items,
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin user permissions', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all permissions.'
        });
      });
  };

  onSelectAllOnPageClick = (): void => {
    this.setState({
      selectedItems: [...this.state.items],
      isSelectAllChecked: false,
      successMessage: ''
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      successMessage: ''
    });
  };

  onCreatePermissionClick = (): void => {
    this.setState({
      isCreateModalOpen: true,
      isEditModalOpen: false,
      form: { ...EMPTY_PERMISSION_FORM, sportIds: [], sports: [] },
      errorMessage: '',
      successMessage: ''
    });
  };

  onEditPermissionClick = (): void => {
    const selectedPermission = this.state.selectedItems[0];

    if (!selectedPermission) {
      return;
    }

    this.setState({
      isCreateModalOpen: false,
      isEditModalOpen: true,
      form: permissionToForm(selectedPermission),
      errorMessage: '',
      successMessage: ''
    });
  };

  onDeletePermissionClick = (): void => {
    const selectedPermission = this.state.selectedItems[0];
    const permissionId = getPermissionId(selectedPermission);

    if (!permissionId) {
      return;
    }

    const preset = selectedPermission.preset || 'permission';
    const shouldDelete = window.confirm(`Remove ${preset} permission from this user?`);

    if (!shouldDelete) {
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    deleteSuperAdminUserPermission(this.props.userId, permissionId)
      .then(() => {
        this.setState(
          {
            isSaving: false,
            selectedItems: [],
            isSelectAllChecked: false,
            successMessage: 'Permission has been removed.'
          },
          () => this.setItems(this.state.currentPage)
        );
      })
      .catch(error => {
        console.error('Can not remove superadmin user permission', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not remove permission. Please try again.'
        });
      });
  };

  onCloseModalClick = (): void => {
    this.setState({
      isCreateModalOpen: false,
      isEditModalOpen: false,
      form: { ...EMPTY_PERMISSION_FORM, sportIds: [], sports: [] },
      errorMessage: ''
    });
  };

  onFormChange = (event): void => {
    const { name, value, type, checked } = event.target;

    this.setState({
      form: {
        ...this.state.form,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  validateCreateData = (data: SuperAdminUserPermissionCreateData, form: PermissionForm): string => {
    if (!data.schoolId) {
      return 'Please select school.';
    }

    if (!data.preset) {
      return 'Please select role.';
    }

    if (hasDuplicateStaffRole(this.state.existingPermissions, data.schoolId, data.preset)) {
      return 'This user already has this staff role in the selected school.';
    }

    if (data.preset === ROLE.PARENT && !data.studentId) {
      return 'Please select child for Parent role.';
    }

    if (
      data.preset === ROLE.PARENT &&
      data.studentId &&
      getLinkedParentStudentIds(this.state.existingPermissions, data.schoolId).indexOf(data.studentId) !== -1
    ) {
      return 'This child is already linked to this user as a parent in the selected school.';
    }

    if (typeof data.priority === 'number' && (data.priority < 0 || data.priority > 10)) {
      return 'Priority should be between 0 and 10.';
    }

    return '';
  };

  validateUpdateData = (data: SuperAdminUserPermissionUpdateData): string => {
    if (typeof data.priority === 'number' && (data.priority < 0 || data.priority > 10)) {
      return 'Priority should be between 0 and 10.';
    }

    return '';
  };

  onSaveCreatePermissionClick = (): void => {
    const form = this.state.form;
    let data: SuperAdminUserPermissionCreateData;

    try {
      data = buildCreateData(form);
    } catch (error) {
      this.setState({ errorMessage: 'Details should be a valid JSON object.' });
      return;
    }

    const validationMessage = this.validateCreateData(data, form);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    createSuperAdminUserPermission(this.props.userId, data)
      .then(() => {
        this.setState(
          {
            isSaving: false,
            isCreateModalOpen: false,
            form: { ...EMPTY_PERMISSION_FORM, sportIds: [], sports: [] },
            successMessage: 'Permission has been created.'
          },
          () => this.setItems(FIRST_PAGE)
        );
      })
      .catch(error => {
        console.error('Can not create superadmin user permission', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not create permission. Please check fields and try again.'
        });
      });
  };

  onSaveEditPermissionClick = (): void => {
    const selectedPermission = this.state.selectedItems[0];
    const permissionId = getPermissionId(selectedPermission);

    if (!permissionId) {
      return;
    }

    const data = buildUpdateData(this.state.form);
    const validationMessage = this.validateUpdateData(data);

    if (validationMessage) {
      this.setState({ errorMessage: validationMessage });
      return;
    }

    this.setState({ isSaving: true, errorMessage: '', successMessage: '' });

    updateSuperAdminUserPermission(this.props.userId, permissionId, data)
      .then(() => {
        this.setState(
          {
            isSaving: false,
            isEditModalOpen: false,
            selectedItems: [],
            form: { ...EMPTY_PERMISSION_FORM, sportIds: [], sports: [] },
            successMessage: 'Permission has been updated.'
          },
          () => this.setItems(this.state.currentPage)
        );
      })
      .catch(error => {
        console.error('Can not update superadmin user permission', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not update permission. Please check fields and try again.'
        });
      });
  };

  renderSportBadges(): React.ReactNode {
    const { form } = this.state;

    if (form.sports.length === 0) {
      return <div className="text-muted mb-2">No sports selected. User will have access to all sports.</div>;
    }

    return (
      <div className="mb-2">
        {form.sports.map((sport, index) => (
          <span
            key={`sport_tag_${getEntityId(sport as NamedEntity)}_${index}`}
            className="badge badge-primary p-1 mr-1 mb-1 font-weight-normal"
            style={{ fontSize: '14px' }}
          >
            {getEntityName(sport as NamedEntity)}
            <span
              aria-hidden="true"
              className="ml-2"
              style={{ cursor: 'pointer' }}
              onClick={() => this.onSportRemove(index)}
            >
              &times;
            </span>
          </span>
        ))}
      </div>
    );
  }

  renderRoleOptions(): React.ReactNode {
    const { form, existingPermissions } = this.state;
    const roleValues = getAllowedRoleValues(form.school, existingPermissions);
    const isSchoolUnion = Boolean(form.school && form.school.kind === 'SchoolUnion');

    return [
      <option key="empty_role" value="">
        Select role
      </option>,
      ...roleValues.map(role => (
        <option key={role} value={role}>
          {isSchoolUnion && role === ROLE.ADMIN
            ? 'Union admin'
            : ROLE_SERVER_TO_CLIENT_MAPPING[role] || formatValue(role)}
        </option>
      ))
    ];
  }

  renderPermissionForm(isEdit: boolean) {
    const { form, isSaving } = this.state;
    const isTeacherOrCoachRole = isTeacherOrCoach(form.preset);
    const isParentRole = isParent(form.preset);
    const isSchoolUnion = Boolean(form.school && form.school.kind === 'SchoolUnion');

    return (
      <div className="card mt-3 mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>{isEdit ? 'Edit permission' : 'Add permission'}</strong>
          <button
            className="btn btn-sm btn-outline-secondary"
            type="button"
            onClick={this.onCloseModalClick}
            disabled={isSaving}
          >
            Close
          </button>
        </div>
        <div className="card-body">
          {!isEdit && (
            <div className="form-row">
              <div className="form-group col-md-6">
                <label>School or union</label>
                <Autocomplete
                  searchFunction={this.searchSchools}
                  getElementTitle={getSchoolTitle}
                  defaultItem={form.school}
                  customClass="mFullWidth mb-3"
                  placeholder="Start typing school or union name"
                  isItemWithImage={true}
                  onSelect={this.onSchoolSelect}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Role</label>
                <select
                  className="form-control"
                  name="preset"
                  value={form.preset}
                  onChange={this.onRoleChange}
                  disabled={!form.schoolId || isSaving}
                >
                  {this.renderRoleOptions()}
                </select>
                {form.schoolId && getAllowedRoleValues(form.school, this.state.existingPermissions).length === 0 && (
                  <small className="form-text text-muted">
                    {isSchoolUnion
                      ? 'This user already has an active admin permission in the selected union.'
                      : 'No more staff roles are available for this user in the selected school. Parent/student roles may still be available if enabled.'}
                  </small>
                )}
              </div>
            </div>
          )}

          {isEdit && (
            <div className="alert alert-info">
              School or union, role and linked child cannot be changed on an existing permission. Remove the permission
              and add a new one if those values are wrong.
            </div>
          )}

          {isEdit && (
            <div className="form-row">
              <div className="form-group col-md-6">
                <label>School or union</label>
                <input
                  className="form-control"
                  value={getSchoolTitle(form.school || { id: form.schoolId, name: form.schoolId })}
                  disabled={true}
                />
              </div>
              <div className="form-group col-md-6">
                <label>Role</label>
                <input
                  className="form-control"
                  value={ROLE_SERVER_TO_CLIENT_MAPPING[form.preset] || formatValue(form.preset)}
                  disabled={true}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Status</label>
              <select
                className="form-control"
                name="status"
                value={form.status}
                onChange={this.onFormChange}
                disabled={isSaving}
              >
                {PERMISSION_STATUS_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group col-md-4">
              <label>Activated at</label>
              <input
                className="form-control"
                name="activatedAt"
                type="datetime-local"
                value={form.activatedAt}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
            <div className="form-group col-md-4">
              <label>Deactivated at</label>
              <input
                className="form-control"
                name="deactivatedAt"
                type="datetime-local"
                value={form.deactivatedAt}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
            </div>
          </div>

          {isStaff(form.preset) && (
            <div className="form-group form-check">
              <input
                className="form-check-input"
                id="isExcludedFromMisIntegration"
                name="isExcludedFromMisIntegration"
                type="checkbox"
                checked={form.isExcludedFromMisIntegration}
                onChange={this.onFormChange}
                disabled={isSaving}
              />
              <label className="form-check-label" htmlFor="isExcludedFromMisIntegration">
                External staff — exclude this permission from MIS integration
              </label>
              <small className="form-text text-muted">
                MIS will not update or remove this school permission while the option is enabled.
              </small>
            </div>
          )}

          {isParentRole && (
            <div className="form-row">
              <div className="form-group col-md-8">
                <label>Child</label>
                {!isEdit && (
                  <small className="form-text text-muted mb-2">
                    Children already linked to this user are hidden from the search results.
                  </small>
                )}
                {!isEdit ? (
                  <Autocomplete
                    searchFunction={this.searchStudents}
                    getElementTitle={getStudentTitle}
                    defaultItem={form.student}
                    customClass="mFullWidth mb-3"
                    placeholder={form.schoolId ? 'Start typing child name' : 'Select school first'}
                    onSelect={this.onStudentSelect}
                    disabled={!form.schoolId || isSaving}
                  />
                ) : (
                  <input
                    className="form-control"
                    value={getStudentTitle(
                      form.student || {
                        id: form.studentId,
                        name: form.studentId
                      }
                    )}
                    disabled={true}
                  />
                )}
              </div>
              <div className="form-group col-md-4">
                <label>Priority</label>
                <input
                  className="form-control"
                  name="priority"
                  type="number"
                  min="0"
                  max="10"
                  value={form.priority}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                />
              </div>
            </div>
          )}

          {isTeacherOrCoachRole && (
            <div className="form-group">
              <label>Sports</label>
              {this.renderSportBadges()}
              <Autocomplete
                searchFunction={this.searchSports}
                getElementTitle={getEntityName}
                customClass="mFullWidth mb-3"
                placeholder={form.schoolId ? 'Start typing sport name' : 'Select school first'}
                onSelect={this.onSportSelect}
                disabled={!form.schoolId || isSaving}
              />
            </div>
          )}

          {!isEdit && (
            <>
              <div className="form-group">
                <label>Comment</label>
                <input
                  className="form-control"
                  name="comment"
                  value={form.comment}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                />
              </div>
              <div className="form-group">
                <label>Details JSON</label>
                <textarea
                  className="form-control"
                  name="detailsText"
                  rows={4}
                  value={form.detailsText}
                  onChange={this.onFormChange}
                  disabled={isSaving}
                  placeholder={'{"key": "value"}'}
                />
              </div>
            </>
          )}

          <button
            className="btn btn-primary mr-2"
            type="button"
            onClick={isEdit ? this.onSaveEditPermissionClick : this.onSaveCreatePermissionClick}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={this.onCloseModalClick}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  render() {
    const {
      items,
      selectedItems,
      currentPage,
      total,
      filters,
      isShowFilter,
      isDataFiltered,
      isSelectAllChecked,
      isLoading,
      isSaving,
      errorMessage,
      successMessage,
      isCreateModalOpen,
      isEditModalOpen
    } = this.state;

    const hasOneSelectedItem = selectedItems.length === 1;
    const actionItems: ActionItem[] = [
      {
        itemText: 'Add permission',
        isActive: true,
        onItemClick: this.onCreatePermissionClick
      },
      {
        itemText: 'Edit selected permission',
        isActive: hasOneSelectedItem,
        onItemClick: this.onEditPermissionClick
      },
      {
        itemText: 'Remove selected permission',
        isActive: hasOneSelectedItem,
        onItemClick: this.onDeletePermissionClick
      }
    ];

    return (
      <div className="SuperAdminUserPermissions">
        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
        {successMessage && !isLoading && <div className="alert alert-success">{successMessage}</div>}
        {(isLoading || isSaving) && <Loader />}

        {isCreateModalOpen && this.renderPermissionForm(false)}
        {isEditModalOpen && this.renderPermissionForm(true)}

        {!isLoading && (
          <Grid2
            dataItems={items}
            filters={filters}
            options={PERMISSION_OPTIONS}
            currentPage={currentPage}
            total={total}
            isSelectAllChecked={isSelectAllChecked}
            isDataFiltered={isDataFiltered}
            sortDirection={'' as TABLE_SORT_DIRECTION}
            sortColumnsName=""
            isShowFilter={isShowFilter}
            dataItemsSelected={selectedItems}
            columns={COLUMNS}
            actionItems={actionItems}
            onItemClick={this.onItemClick}
            onSortClick={this.onTableSortClick}
            onApplyFilterClick={this.onApplyFilterClick}
            onClearFilterClick={this.onClearFilterClick}
            onTableFilterChange={this.onTableFilterChange}
            onTableFilterClick={this.onTableFilterClick}
            setCurrentPageParams={this.setCurrentPageParams}
            onSelectAllClick={this.onSelectAllClick}
            onSelectAllOnPageClick={this.onSelectAllOnPageClick}
            onUnselectAllClick={this.onUnselectAllClick}
          />
        )}
      </div>
    );
  }
}
